/*
 * Classify the selected picture (AIVA-5376).
 *
 * The classify endpoint reads its image from Azure Blob Storage, so one
 * classification is a three-step chain:
 *
 *   capture the shape's pixels  →  blob-sas + PUT  →  POST image/classify
 *
 * All three steps already exist for other features — `slideImageCapture` and
 * `blobImageUpload` are the same modules Photo Headshot and Sketch to Slide
 * drive, parameterised by messages and telemetry reasons. This module supplies
 * the classification-flavoured ones and joins them up.
 *
 * Every failure resolves to `null` rather than rejecting. A classification is an
 * enhancement: it turns the generic image chip set into a content-specific one.
 * If it can't run, the generic set is still a correct and usable offer, so the
 * dock must never surface an error for it — the failure is logged instead.
 */

import type { HttpClient, HttpRequestOptions } from "../../../services/deckorateApi";
import {
  captureSelectedImage,
  readSelectedImageShape,
  type SlideImageMessages,
  type SlideImageReasons,
} from "../../shared/services/slideImageCapture";
import { uploadImageBlob } from "../../shared/services/blobImageUpload";
import { devTrace } from "../../shared/services/devTrace";
import {
  CLASSIFY_TRACE_SCOPE,
  getErrorMessage,
  logClassifyError,
  postClassifyImage,
  type ImageClassification,
} from "./classifyImageApi";

/**
 * These are never shown. The capture helper needs a message set, but this path
 * is only entered once the dock has already established that exactly one
 * picture is selected, and its failures are logged rather than surfaced. They
 * exist to make a log line readable, not to be read by a user.
 */
const MESSAGES: SlideImageMessages = {
  pptOnly: "Image classification works only in PowerPoint.",
  noSelection: "No image is selected.",
  multiSelection: "More than one shape is selected.",
  notAnImage: "The selected shape is not a picture.",
  captureUnsupported: "Reading the selected image needs PowerPointApi 1.5+.",
  itemLabel: "image",
  // Fail fast on a malformed capture: uploading an empty blob would spend a
  // vision call to classify nothing.
  strictCapture: true,
};

const REASONS: SlideImageReasons = {
  placeholderTypeCheck: "classify-placeholder-type-check",
  slideSizeRead: "classify-slide-size-read",
  slideCapture: "classify-slide-capture",
  cropEncode: "classify-crop-encode",
  cropEmptyResult: "classify-crop-empty-result",
};

const UPLOAD_MESSAGES = {
  blobSasFailed: "Couldn't get a place to upload the image for classification.",
  blobPutFailed: "Couldn't upload the image for classification.",
};

/** One reason per distinct failure point, so a log line says which step broke. */
const UPLOAD_REASONS = {
  formatSniff: "classify-blob-format-sniff",
  sasRequest: "classify-blob-sas-request",
  sasVersionParse: "classify-blob-sas-version-parse",
  putEncode: "classify-blob-put-encode",
  putNetwork: "classify-blob-put-network",
  putBodyRead: "classify-blob-put-body-read",
};

/** File name sent with the blob-sas request. The extension is re-derived from
 *  the bytes' own signature by the upload helper, so this is a label only. */
const UPLOAD_FILE_NAME = "chip-classify.png";

/** A classification, paired with the shape it describes so it can be cached. */
export interface ClassifiedSelection {
  /** Office.js shape id — the cache key, and how a stale result is detected. */
  readonly shapeId: string;
  readonly classification: ImageClassification;
}

export interface ClassifySelectionConfig {
  readonly http: HttpClient;
  readonly clientId: number;
  readonly options?: HttpRequestOptions;
}

/**
 * Capture, upload and classify the currently selected picture.
 *
 * Resolves to `null` on any failure, and also when the API returns an image type
 * this build doesn't recognise — in both cases the caller keeps the generic
 * image chips.
 */
export async function classifySelectedImage(
  config: ClassifySelectionConfig,
): Promise<ClassifiedSelection | null> {
  const options = config.options ?? {};
  try {
    // One trace per step. The chain has five links and every one of them is
    // designed to fail quietly, so without these a "nothing happened" report
    // gives no way to tell which link broke.
    devTrace(CLASSIFY_TRACE_SCOPE, "start", { clientId: config.clientId });

    const shape = await readSelectedImageShape(MESSAGES, logClassifyError, REASONS);
    devTrace(CLASSIFY_TRACE_SCOPE, "read selected picture", { shapeId: shape.shapeId });

    const { base64, mimeType } = await captureSelectedImage(shape, MESSAGES, logClassifyError, REASONS);
    // Length only — never the bytes themselves.
    devTrace(CLASSIFY_TRACE_SCOPE, "captured pixels", { mimeType, base64Length: base64.length });

    const blob = await uploadImageBlob({
      http: config.http,
      clientId: config.clientId,
      base64,
      mimeType,
      fileName: UPLOAD_FILE_NAME,
      options,
      messages: UPLOAD_MESSAGES,
      onError: logClassifyError,
      reasons: UPLOAD_REASONS,
    });

    devTrace(CLASSIFY_TRACE_SCOPE, "uploaded blob", {
      blobName: blob.blobName,
      blobContainer: blob.blobContainer,
    });

    const classification = await postClassifyImage(config.http, blob, options);
    if (!classification) return null;

    devTrace(CLASSIFY_TRACE_SCOPE, "classified", {
      shapeId: shape.shapeId,
      kind: classification.kind,
      fromVision: classification.fromVision,
    });
    return { shapeId: shape.shapeId, classification };
  } catch (error) {
    logClassifyError("classify-selection-failed", getErrorMessage(error, "Image classification failed"));
    return null;
  }
}
